/*
  La Matcha — header group behaviour.

    <announcement-bar>  rotating messages with previous / next buttons
    <site-header>       mega menu, mobile menu helpers and the cart button state
    <promo-slider>      neon product slider inside the mobile menu

  The announcement bar and header sections both load this file; it runs once.
  (The reorder notice of the header group has its own file: assets/reorder.js.)
  The elements set themselves up again whenever the theme editor re-renders a section.
*/
(() => {
  const LM = (window.LM = window.LM || {});
  if (LM.headerLoaded) return;
  LM.headerLoaded = true;

  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const desktop = window.matchMedia('(min-width: 1100px)');
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduceMotion = () => motion.matches;
  const mod = (value, count) => ((value % count) + count) % count;
  const megaIsOpen = () => !!document.querySelector('[data-mega-trigger][aria-expanded="true"]');
  const closeDialog = (dialog) => (LM.closeDialog ? LM.closeDialog(dialog) : dialog.close());

  /* ---------- <announcement-bar> ----------
     Children: [data-announcement] messages (one has .is-active), [data-announcement-prev],
     [data-announcement-next], [data-announcement-live] (the aria-live wrapper).
     data-interval (ms, default 4500); data-manual turns the automatic rotation off.
     Rotation also stops under reduced motion, on hover, on keyboard focus, in a hidden tab and
     while a mega menu is open. The live region is silent while messages rotate on their own. */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-announcement]')];
      this.live = this.querySelector('[data-announcement-live]');
      this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      this.interval = parseInt(this.dataset.interval, 10) || 4500;
      this.holds = new Set();

      if (!this.bound) {
        this.bound = true;
        this.addEventListener('click', (event) => {
          const previous = event.target.closest('[data-announcement-prev]');
          const next = event.target.closest('[data-announcement-next]');
          if (!previous && !next) return;
          this.go(this.index + (next ? 1 : -1), next ? 'next' : 'prev');
          this.start();
        });
        this.addEventListener('pointerenter', () => this.hold('hover'));
        this.addEventListener('pointerleave', () => this.release('hover'));
        this.addEventListener('focusin', () => this.hold('focus'));
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.release('focus');
        });
        this.addEventListener('animationend', (event) => {
          const item = event.target.closest('[data-announcement]');
          if (!item) return;
          item.classList.remove('is-leaving');
          item.removeAttribute('data-enter');
          item.removeAttribute('data-leave');
        });
        this.addEventListener('shopify:block:select', (event) => {
          const item = event.target.closest('[data-announcement]');
          this.hold('editor');
          if (item) this.go(this.items.indexOf(item), 'auto');
        });
        this.addEventListener('shopify:block:deselect', () => this.release('editor'));
      }

      this.onVisibility = () => (document.hidden ? this.hold('hidden') : this.release('hidden'));
      this.onMotion = () => this.start();
      document.addEventListener('visibilitychange', this.onVisibility);
      motion.addEventListener('change', this.onMotion);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
      motion.removeEventListener('change', this.onMotion);
    }

    hold(reason) {
      this.holds.add(reason);
      this.stop();
    }

    release(reason) {
      this.holds.delete(reason);
      this.start();
    }

    start() {
      this.stop();
      if (this.items.length < 2 || this.hasAttribute('data-manual') || reduceMotion() || this.holds.size) return;
      if (this.live) this.live.setAttribute('aria-live', 'off');
      this.timer = window.setInterval(() => {
        if (!megaIsOpen()) this.go(this.index + 1, 'auto');
      }, this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
      if (this.live) this.live.setAttribute('aria-live', 'polite');
    }

    /* direction: 'next' slides the new message in from the left, 'prev' and 'auto' from the right. */
    go(target, direction) {
      const count = this.items.length;
      if (count < 2) return;
      const index = mod(target, count);
      if (index === this.index) return;
      const from = this.items[this.index];
      const to = this.items[index];
      this.items.forEach((item) => {
        item.classList.remove('is-active', 'is-leaving');
        item.removeAttribute('data-enter');
        item.removeAttribute('data-leave');
        item.setAttribute('aria-hidden', 'true');
      });
      from.classList.add('is-leaving');
      from.setAttribute('data-leave', direction === 'next' ? 'r' : 'l');
      to.classList.add('is-active');
      to.setAttribute('data-enter', direction === 'next' ? 'l' : 'r');
      to.removeAttribute('aria-hidden');
      this.index = index;
    }
  }

  /* ---------- Mobile menu preview (theme editor) ----------
     Opens #MobileMenu without the modal top layer, so selecting a block in the editor can reveal
     it without moving keyboard focus out of the editor sidebar. */
  const menuPreview = {
    timer: null,
    open(dialog) {
      window.clearTimeout(this.timer);
      if (!dialog || dialog.open) return;
      dialog.setAttribute('data-preview', '');
      dialog.setAttribute('open', '');
    },
    close(dialog) {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        if (!dialog || !dialog.open || !dialog.hasAttribute('data-preview')) return;
        dialog.removeAttribute('data-preview');
        dialog.close();
      }, 80);
    },
  };

  /* ---------- <site-header> ----------
     Mega menu: [data-mega-trigger] buttons (aria-expanded, aria-controls) and their
     [data-mega-panel] panels. Opens on hover and on click / Enter / Space; closes when the pointer
     leaves the header, on Escape, when focus moves on, on an outside click and when a dialog opens.
     Also keeps the cart button ([data-cart-link]) in step with lm:cart:updated and tidies the
     mobile menu dialog (#MobileMenu). */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.menu = this.querySelector('dialog.mobile-menu');
      this.active = null;
      this.locked = false;
      this.hoveredAt = 0;

      if (!this.bound) {
        this.bound = true;
        this.querySelectorAll('.header__item > .header__link').forEach((link) => {
          link.addEventListener('pointerenter', (event) => {
            if (event.pointerType === 'touch') return;
            if (link.hasAttribute('data-mega-trigger')) this.open(link, true);
            else this.close();
          });
        });
        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'touch') this.close();
        });
        this.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-mega-trigger]');
          if (trigger) {
            if (this.active !== trigger) this.open(trigger);
            // A click right after the hover that opened the panel must not close it again.
            else if (performance.now() - this.hoveredAt > 350) this.close();
            return;
          }
          if (event.target.closest('[data-mega-panel] a[href]')) this.close();
        });
        this.addEventListener('focusout', (event) => {
          if (!this.active || !event.relatedTarget) return;
          const item = this.active.closest('.header__item');
          if (item && !item.contains(event.relatedTarget)) this.close();
        });
        this.addEventListener('shopify:block:select', (event) => {
          const panel = event.target.closest('[data-mega-panel]');
          if (!panel) return;
          if (desktop.matches) {
            this.locked = true;
            this.open(this.querySelector(`[data-mega-trigger][aria-controls="${panel.id}"]`));
            return;
          }
          menuPreview.open(this.menu);
          const blockId = event.detail && event.detail.blockId;
          const details = this.menu && blockId ? this.menu.querySelector(`[data-block-id="${blockId}"]`) : null;
          if (details) details.open = true;
        });
        this.addEventListener('shopify:block:deselect', (event) => {
          if (!event.target.closest('[data-mega-panel]')) return;
          this.locked = false;
          this.close();
          menuPreview.close(this.menu);
        });
      }

      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.active || this.locked) return;
        const trigger = this.active;
        const hadFocus = this.contains(document.activeElement);
        this.close();
        if (hadFocus) trigger.focus();
      };
      this.onDocumentClick = (event) => {
        if (!this.active) return;
        const item = this.active.closest('.header__item');
        if (item && !item.contains(event.target)) this.close();
      };
      this.onDialogOpen = (event) => {
        this.close();
        // Every visit to the mobile menu starts with its accordions closed.
        if (event.detail && event.detail.dialog === this.menu) {
          this.menu.querySelectorAll('details[open]').forEach((details) => (details.open = false));
        }
      };
      this.onCart = (event) => this.updateCart(event.detail && event.detail.cart);
      this.onBreakpoint = () => {
        if (!desktop.matches) this.close(true);
        else if (!designMode && this.menu && this.menu.open) closeDialog(this.menu);
      };
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('lm:dialog:open', this.onDialogOpen);
      document.addEventListener('lm:cart:updated', this.onCart);
      desktop.addEventListener('change', this.onBreakpoint);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('lm:dialog:open', this.onDialogOpen);
      document.removeEventListener('lm:cart:updated', this.onCart);
      desktop.removeEventListener('change', this.onBreakpoint);
    }

    panelFor(trigger) {
      return document.getElementById(trigger.getAttribute('aria-controls'));
    }

    open(trigger, byHover) {
      if (!trigger || !desktop.matches || this.active === trigger) return;
      const panel = this.panelFor(trigger);
      if (!panel) return;
      const swap = !!this.active;
      this.close(true);
      panel.classList.toggle('is-swap', swap);
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.active = trigger;
      if (byHover) this.hoveredAt = performance.now();
    }

    close(force) {
      if (!this.active || (this.locked && !force)) return;
      const panel = this.panelFor(this.active);
      if (panel) panel.hidden = true;
      this.active.setAttribute('aria-expanded', 'false');
      this.active = null;
    }

    updateCart(cart) {
      const link = this.querySelector('[data-cart-link]');
      if (!link || !cart || typeof cart.item_count !== 'number') return;
      const count = cart.item_count;
      link.classList.toggle('is-empty', count === 0);
      const label = count === 1 ? link.dataset.labelOne : link.dataset.labelOther;
      if (label) link.setAttribute('aria-label', label.replace('[count]', count));
    }
  }

  /* ---------- <promo-slider> ----------
     Children: [data-promo-track] with [data-promo-slide] slides, [data-promo-dot] buttons.
     data-interval (ms, default 3500). Advances only while its dialog is open, never under reduced
     motion; swipe or drag to change slide. */
  class PromoSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-promo-track]');
      this.slides = [...this.querySelectorAll('[data-promo-slide]')];
      this.dots = [...this.querySelectorAll('[data-promo-dot]')];
      this.dialog = this.closest('dialog');
      this.interval = parseInt(this.dataset.interval, 10) || 3500;
      this.index = 0;
      this.holds = new Set();
      if (!this.track || !this.slides.length) return;

      if (!this.bound) {
        this.bound = true;
        this.bindSwipe();
        this.addEventListener('click', (event) => {
          const dot = event.target.closest('[data-promo-dot]');
          if (!dot) return;
          this.go(this.dots.indexOf(dot));
          this.start();
        });
        this.addEventListener('focusin', () => this.hold('focus'));
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.release('focus');
        });
        this.addEventListener('shopify:block:select', (event) => {
          const slide = event.target.closest('[data-promo-slide]');
          if (!slide) return;
          this.hold('editor');
          menuPreview.open(this.dialog);
          this.go(this.slides.indexOf(slide));
        });
        this.addEventListener('shopify:block:deselect', () => {
          this.release('editor');
          menuPreview.close(this.dialog);
        });
      }

      this.onDialog = () => this.start();
      document.addEventListener('lm:dialog:open', this.onDialog);
      document.addEventListener('lm:dialog:close', this.onDialog);
      this.go(0);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('lm:dialog:open', this.onDialog);
      document.removeEventListener('lm:dialog:close', this.onDialog);
    }

    hold(reason) {
      this.holds.add(reason);
      this.stop();
    }

    release(reason) {
      this.holds.delete(reason);
      this.start();
    }

    start() {
      this.stop();
      const visible = !this.dialog || this.dialog.open;
      if (this.slides.length < 2 || !visible || reduceMotion() || this.holds.size) return;
      this.timer = window.setInterval(() => this.go(this.index + 1), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    go(target) {
      this.index = mod(target, this.slides.length);
      this.style.setProperty('--promo-index', this.index);
      this.slides.forEach((slide, i) => {
        const current = i === this.index;
        slide.inert = !current;
        if (current) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    bindSwipe() {
      let tracking = false;
      let moved = false;
      let startX = 0;
      let startY = 0;
      let deltaX = 0;

      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('[data-promo-dot]') || this.slides.length < 2) return;
        tracking = true;
        moved = false;
        deltaX = 0;
        startX = event.clientX;
        startY = event.clientY;
      });

      this.addEventListener('pointermove', (event) => {
        if (!tracking) return;
        deltaX = event.clientX - startX;
        if (!moved) {
          const deltaY = event.clientY - startY;
          if (Math.abs(deltaX) < 8 || Math.abs(deltaX) < Math.abs(deltaY)) return;
          moved = true;
          this.classList.add('is-dragging');
          this.stop();
          if (this.setPointerCapture) {
            try {
              this.setPointerCapture(event.pointerId);
            } catch (error) {
              /* The pointer is already gone; the drag simply ends on pointerup. */
            }
          }
        }
        this.style.setProperty('--promo-drag', `${deltaX}px`);
      });

      const end = () => {
        if (!tracking) return;
        tracking = false;
        this.classList.remove('is-dragging');
        this.style.removeProperty('--promo-drag');
        if (!moved) return;
        if (Math.abs(deltaX) > 40) this.go(this.index + (deltaX < 0 ? 1 : -1));
        this.start();
      };
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);
      this.addEventListener('dragstart', (event) => event.preventDefault());

      // A swipe must not follow the link underneath.
      this.addEventListener(
        'click',
        (event) => {
          if (!moved) return;
          moved = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    }
  }

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };
  define('announcement-bar', AnnouncementBar);
  define('site-header', SiteHeader);
  define('promo-slider', PromoSlider);
})();
